import { useReducedMotion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import Play from 'components/Icons/Play';
import Player from 'components/player/Player';
import { isMobile } from 'helpers/getDeviceInfo';
import theme from 'theme/theme';

import { COACHES } from '../../constants';
import CoachRing from '../CoachRing';
import TypingWord from '../TypingWord';
import WaterBackground from '../WaterBackground';

const COACH_INTERVAL_MS = 4800;

// Copy and ring stack at the prototype's 1100; phone sizing uses the theme's md.
const STACKED = '@media (max-width: 1100px)';

type Props = {
  videoSrc: string;
};

const CoachHeroSection = ({ videoSrc }: Props) => {
  const reduceMotion = useReducedMotion();

  const [isPlaying, setIsPlaying] = useState(false);
  const [activeCoach, setActiveCoach] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Headline word and ring share one clock; picking a coach restarts it.
  useEffect(() => {
    if (isPaused || reduceMotion) {
      return undefined;
    }

    const timer = setTimeout(
      () => setActiveCoach((prev) => (prev + 1) % COACHES.length),
      COACH_INTERVAL_MS
    );

    return () => clearTimeout(timer);
  }, [activeCoach, isPaused, reduceMotion]);

  return (
    <Container>
      <Background>
        <WaterBackground />
      </Background>
      <FadeOverlay />
      <Content>
        <TextContentTop>
          <Eyebrow>Health coaching app</Eyebrow>
          <Title
            onPointerEnter={(e) => setIsPaused(e.pointerType === 'mouse')}
            onPointerLeave={() => setIsPaused(false)}
          >
            Your <HeadlineWord word={COACHES[activeCoach].word} /> has a
            pattern.
            <br />
            Your coach can help improve it.
          </Title>
        </TextContentTop>
        <Ring
          activeIndex={activeCoach}
          onSelect={setActiveCoach}
          onHoverChange={setIsPaused}
        />
        <TextContentBottom>
          <Paragraph>
            Your coach reads your sleep, food, movement, weight and stress
            together. Advice bespoke to you, not the average person.
          </Paragraph>
          <MobileWaveStrip>
            <ButtonsRow>
              <SeeHowItWorks type="button" onClick={() => setIsPlaying(true)}>
                <span>See how the app works</span>
                <PlayCircle>
                  <SeeHowItWorksPlay color={theme.colors.white} />
                </PlayCircle>
              </SeeHowItWorks>
            </ButtonsRow>
          </MobileWaveStrip>
        </TextContentBottom>
      </Content>
      <Template alt="template" />
      <Player
        src={videoSrc}
        isOpen={isPlaying}
        closeVideo={() => setIsPlaying(false)}
        isMobile={isMobile}
      />
    </Container>
  );
};

const TextContentTop = styled.div`
  grid-area: top;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: end;
  min-width: 0;
  margin-top: -90px;

  ${STACKED} {
    display: contents;
  }
`;

const TextContentBottom = styled.div`
  grid-area: bottom;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: start;
  min-width: 0;

  ${STACKED} {
    display: contents;
  }
`;

const Paragraph = styled.p`
  margin-top: 24px;
  color: ${({ theme }) => theme.colors.blueBayoux};
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  text-align: left;
  max-width: 770px;

  ${STACKED} {
    max-width: 540px;
    margin-bottom: 0;
    text-align: center;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin-top: 40px;

  ${STACKED} {
    margin-top: 0;
  }
`;

const MobileWaveStrip = styled.div`
  display: contents;

  ${STACKED} {
    order: 2;
    display: flex;
    justify-content: center;
  }
`;

const SeeHowItWorks = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 312px;
  max-width: 100%;
  height: 52px;
  box-sizing: border-box;
  padding: 0 24px;
  border: 2px solid ${({ theme }) => theme.colors.lightBlue};
  border-radius: 30px;
  background-color: ${({ theme }) => theme.colors.white};
  white-space: nowrap;
  cursor: pointer;

  span {
    color: ${({ theme }) => theme.colors.lightBlue};
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    text-transform: uppercase;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: calc(100vw - 40px);
  }
`;

const PlayCircle = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightBlue};
`;

const SeeHowItWorksPlay = styled(Play)`
  width: 6px;
  height: 7px;
  margin-left: 2px;
`;

const Eyebrow = styled.p`
  font-weight: 700;
  font-size: 13px;
  line-height: 100%;
  letter-spacing: 2.2px;
  text-transform: uppercase;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.blueBayoux};
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 42px;
  line-height: 49.56px;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
  max-width: 700px;

  ${STACKED} {
    max-width: none;
    font-size: 38px;
    line-height: 1.18;
    text-align: center;
    white-space: nowrap;
  }

  /* Each sentence stays on one line: the longer one is ~14.6em wide. */
  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: min(27px, calc((100vw - 40px) / 14.6));
  }
`;

const HeadlineWord = styled(TypingWord)`
  color: ${({ theme }) => theme.colors.secondaryV1};
`;

const Ring = styled(CoachRing)`
  grid-area: diagram;
  align-self: center;
  margin: 60px;

  ${STACKED} {
    order: 1;
    margin: 64px 0 40px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: 56px;
    zoom: min(1, tan(atan2(100vw, 390px)));
    zoom: min(1, 100vw / 390px);
  }
`;

const Content = styled.article`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'top diagram'
    'bottom diagram';
  column-gap: 40px;
  width: 100%;
  max-width: 1460px;
  margin: -40px auto 0;
  position: relative;
  z-index: 2;
  padding: 0 29px 20px;
  box-sizing: border-box;

  ${STACKED} {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 0;
    padding: 0 40px 20px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 0 20px 50px;
  }
`;

const Container = styled.section`
  position: relative;
  padding-top: 122px;
  background: ${({ theme }) => theme.colors.lightYellow};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${STACKED} {
    padding-top: 100px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-top: 40px;
  }
`;

// Stacked, the water fills the hero under a cream fade and both share a
// curved bottom edge, as in the prototype.
const stackedClip = css`
  ${STACKED} {
    clip-path: ellipse(110% 97% at 50% 0%);
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    clip-path: ellipse(160% 93% at 50% 0%);
  }
`;

const FadeOverlay = styled.div`
  position: absolute;
  top: 254px;
  left: 0;
  width: 100%;
  height: 392px;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.lightYellow} 0%,
    rgb(247 247 242 / 0%) 100%
  );
  z-index: 1;

  ${STACKED} {
    top: 0;
    height: 100%;
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.colors.lightYellow} 0%,
      ${({ theme }) => theme.colors.lightYellow} 50%,
      rgb(247 247 242 / 70%) 60%,
      rgb(247 247 242 / 0%) 74%
    );
  }

  ${stackedClip}
`;

const Background = styled.div`
  position: absolute;
  top: 254px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 575px;
  z-index: 1;

  ${STACKED} {
    top: 0;
    left: 0;
    transform: none;
    max-width: none;
    height: 100%;
  }

  ${stackedClip}
`;

const Template = styled.img`
  position: relative;
  z-index: 2;
  display: block;
  width: 100%;
  height: 190px;
  margin: 0 auto -110px;
  object-fit: cover;
  object-position: top;
  content: url('/images/intro/template_hero.png');

  ${STACKED} {
    display: none;
  }
`;

export default CoachHeroSection;
